// Nested layout route for /admin/:slug/*. Mounts once on entry to a
// station's admin views and stays mounted across tab clicks within the
// same station — the previous architecture re-mounted the gate and the
// layout on every tab because each page wrapped them itself.
//
// Responsibilities:
//   • Owns PublisherAdminGate (auth + station-access + grant check).
//   • Owns PublisherAdminLayout (sidebar + main content shell).
//   • Provides the outlet context, mode='single' or mode='aggregated'.
//
// CTV-759 / ADR-0036: `/admin/all` is the All-stations sentinel and is
// handled HERE, before PublisherAdminGate — the gate resolves the slug
// through GET /stations/:slug and would 404 it. The branch stays inside the
// existing /admin/:slug route element rather than becoming a sibling
// top-level route, because main.tsx mounts AuthProvider per top-level route
// through wrap(): a sibling would remount auth and refire /stations/my-admin,
// /memberships/mine and the grant RPC on every scope change.
//
// Child routes render only their unique content; station-bound pages read
// config + slug + user via useSingleStationContext(), scope-flexible pages
// via useAdminContext() with a ctx.mode branch.

import React from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import PublisherAdminGate from './PublisherAdminGate';
import AdminAuthGate from './AdminAuthGate';
import PublisherAdminLayout from './PublisherAdminLayout';
import {
  ALL_STATIONS_SLUG,
  rememberAdminScope,
  rememberAdminStation,
  rememberedAdminStation,
} from './lastAdminStation';
import { isScopeFlexiblePath } from './adminScopeTabs';
import { useAuth } from '../auth/useAuth';
import type { AdminContext } from './useAdminContext';

const AdminShell: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Two remembered values (ADR-0036): the CURRENT scope, which may be `all`,
  // and the LAST SINGLE STATION, which never is. rememberAdminStation drops
  // the sentinel itself, so /admin/all leaves the collapse target intact —
  // that is what makes Scope collapse land somewhere real.
  React.useEffect(() => {
    if (!slug) return;
    rememberAdminStation(slug);
    rememberAdminScope(slug);
  }, [slug]);

  if (slug === ALL_STATIONS_SLUG) return <AggregatedAdminShell />;

  return (
    <PublisherAdminGate slug={slug}>
      {({ config, user, slug: gatedSlug }) => {
        const context: AdminContext = {
          mode: 'single',
          config,
          slug: gatedSlug,
          user: {
            id: user.id ?? '',
            email: user.email ?? '',
            rawRole: (user as { rawRole?: string }).rawRole,
          },
        };
        return (
          <PublisherAdminLayout context={context}>
            <Outlet context={context} />
          </PublisherAdminLayout>
        );
      }}
    </PublisherAdminGate>
  );
};

/**
 * The /admin/all branch. No station to resolve, so it reuses only the
 * station-agnostic auth + grant checks and gets its station list from
 * useAuth.myAdminStations, which is already fetched once per session.
 */
const AggregatedAdminShell: React.FC = () => {
  const location = useLocation();
  const { myAdminStations } = useAuth();

  return (
    <AdminAuthGate>
      {(user) => {
        if (myAdminStations == null) {
          return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
              Loading…
            </div>
          );
        }

        // A station with no slug has no admin URL, so it cannot participate
        // in a scope that is carried in the URL's slug segment.
        const stations = myAdminStations.filter((s) => s.slug);
        if (stations.length === 0) return <Navigate to="/access-denied" replace />;
        // All stations is meaningless for one station, and the selector
        // already hides itself in that case.
        if (stations.length === 1) {
          return <Navigate to={`/admin/${stations[0].slug}`} replace />;
        }

        // The Scope collapse target. `stations` is ordered by name
        // server-side, which is what makes the fallback deterministic. The
        // membership check matters: a remembered slug can outlive the grant
        // that produced it.
        const remembered = rememberedAdminStation();
        const collapseSlug =
          remembered && stations.some((s) => s.slug === remembered)
            ? remembered
            : (stations[0].slug as string);

        // /admin/all/<station-bound-tab> is a URL a user can type and it must
        // never render. Collapsing here rather than in each page keeps the
        // rule identical whether the transition came from a click or the
        // address bar.
        if (!isScopeFlexiblePath(location.pathname)) {
          const rest = location.pathname.replace(/^\/admin\/[^/]+/, '');
          return <Navigate to={`/admin/${collapseSlug}${rest}`} replace />;
        }

        const context: AdminContext = {
          mode: 'aggregated',
          stations,
          collapseSlug,
          user: {
            id: user.id ?? '',
            email: user.email ?? '',
            rawRole: (user as { rawRole?: string }).rawRole,
          },
        };
        return (
          <PublisherAdminLayout context={context}>
            <Outlet context={context} />
          </PublisherAdminLayout>
        );
      }}
    </AdminAuthGate>
  );
};

export default AdminShell;
